import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { IMAGES } from "../constants/site";
import Reveal from "../components/Reveal";
import { useSeo } from "../hooks/useSeo";

function ModelDetail({ model, image, flip }) {
  const { t } = useLanguage();
  const labels = t.fleetPage.labels;
  const data = t.fleetPage.models[model.tkey];

  const specs = [
    { l: labels.range, v: model.range },
    { l: labels.accel, v: model.accel },
    { l: labels.seats, v: model.seats },
    { l: labels.luggage, v: model.luggage },
    { l: labels.drive, v: data.drive }
  ];

  return (
    <section className="py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-10 grid gap-10 lg:grid-cols-2 lg:items-center">
        <Reveal className={flip ? "lg:order-2" : ""}>
          <div className="group relative rounded-2xl overflow-hidden border border-white/10">
            <img
              src={image}
              alt={data.name}
              className="h-[280px] lg:h-[420px] w-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.05]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070709]/80 via-transparent to-transparent" />
            <span className="absolute top-5 left-5 font-mono2 text-[10px] uppercase tracking-[0.25em] text-gold-soft bg-black/55 backdrop-blur px-3 py-1.5 rounded-full border border-gold/25">
              {data.category}
            </span>
          </div>
        </Reveal>

        <Reveal delay={0.1} className={flip ? "lg:order-1" : ""}>
          <h2 className="font-display text-2xl lg:text-4xl font-bold tracking-tight text-white" data-testid={`fleet-title-${model.id}`}>
            {data.name}
          </h2>
          <p className="mt-4 text-slate-400 leading-relaxed max-w-[56ch]">{data.desc}</p>

          <dl className="mt-8 border-t border-white/10" data-testid={`fleet-specs-${model.id}`}>
            {specs.map(({ l, v }) => (
              <div key={l} className="flex items-center justify-between border-b border-white/10 py-3.5">
                <dt className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-slate-500">{l}</dt>
                <dd className="font-mono2 text-base text-gold-soft">{v}</dd>
              </div>
            ))}
          </dl>

          <p className="mt-8 eyebrow">{labels.features}</p>
          <ul className="mt-4 flex flex-wrap gap-2">
            {data.features.map((f) => (
              <li key={f} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-xs text-slate-300">
                <Check size={12} className="text-gold" /> {f}
              </li>
            ))}
          </ul>

          <Link to="/contact" data-testid={`fleet-book-${model.id}`} className="btn-gold mt-9 text-xs font-mono2 uppercase tracking-[0.18em]">
            {t.fleetPage.bookCta} <ArrowRight size={14} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Comparison() {
  const { t } = useLanguage();
  const rows = [
    { l: t.fleetPage.labels.range, m3: "629 km", my: "533 km" },
    { l: t.fleetPage.labels.accel, m3: "4.4s", my: "5.0s" },
    { l: t.fleetPage.labels.seats, m3: "4", my: "4–5" },
    { l: t.fleetPage.labels.luggage, m3: "2+2", my: "4" }
  ];

  return (
    <section className="border-t border-white/10 bg-[#090a0e] py-20 lg:py-28">
      <div className="mx-auto max-w-5xl px-5 lg:px-10">
        <Reveal>
          <p className="eyebrow text-center">{t.fleetPage.compareEyebrow}</p>
          <h2 className="mt-4 text-center font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight">
            {t.fleetPage.compareTitle}
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-12 grid gap-3 sm:hidden" data-testid="fleet-comparison-mobile-cards">
            {rows.map((r) => (
              <div key={r.l} className="rounded-xl border border-white/10 bg-[#0F1015] p-4">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-slate-500">{r.l}</p>
                <p className="mt-3 flex items-baseline justify-between gap-3 text-sm">
                  <span className="text-slate-400">{t.fleetPage.models.m3.name}</span>
                  <span className="font-mono2 text-gold-soft">{r.m3}</span>
                </p>
                <p className="mt-2 flex items-baseline justify-between gap-3 text-sm">
                  <span className="text-slate-400">{t.fleetPage.models.my.name}</span>
                  <span className="font-mono2 text-gold-soft">{r.my}</span>
                </p>
              </div>
            ))}
          </div>
          <div className="mt-12 hidden sm:block overflow-x-auto rounded-2xl border border-white/10">
            <table className="w-full min-w-[560px] text-left" data-testid="fleet-comparison-table">
              <thead>
                <tr className="bg-white/[0.03]">
                  <th className="p-5" />
                  <th className="p-5 font-display text-base font-bold text-white">{t.fleetPage.models.m3.name}</th>
                  <th className="p-5 font-display text-base font-bold text-white">{t.fleetPage.models.my.name}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.l} className="border-t border-white/10">
                    <td className="p-5 font-mono2 text-[11px] uppercase tracking-[0.18em] text-slate-500">{r.l}</td>
                    <td className="p-5 font-mono2 text-gold-soft">{r.m3}</td>
                    <td className="p-5 font-mono2 text-gold-soft">{r.my}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Fleet() {
  const { t } = useLanguage();
  useSeo(
    "Tesla Model 3 & Model Y Fleet | MIRAL GROUP",
    "Explore the MIRAL fleet: Tesla Model 3 Highland (629 km WLTP) and Model Y Long Range (533 km WLTP) — full specs, comfort features and comparison."
  );
  const models = {
    m3: { id: "model-3", tkey: "m3", range: "629 km", accel: "4.4s", seats: "4", luggage: "2 large + 2 hand" },
    my: { id: "model-y", tkey: "my", range: "533 km", accel: "5.0s", seats: "4–5", luggage: "4 large suitcases" }
  };

  return (
    <main className="pt-[72px]">
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal>
            <p className="eyebrow">{t.fleetPage.eyebrow}</p>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight">
              {t.fleetPage.title}
            </h1>
            <p className="mt-6 max-w-[60ch] text-lg text-slate-300">{t.fleetPage.sub}</p>
          </Reveal>
        </div>
      </section>

      <ModelDetail model={models.m3} image={IMAGES.m3} flip={false} />
      <ModelDetail model={models.my} image={IMAGES.my} flip={true} />
      <Comparison />
    </main>
  );
}
